import { isFeatureEnabled } from "@/env";
import { GameEngines, GameSystems, Universes } from "./types";

export const FEATURE_FLAGS = {
  V3_6: "v3.6",
  QUEST_SWARM: "questSwarm",
  QUEST_MAP: "questMap",
  REMOVE_ALIAS_MODE: "removeAliasMode",
  QUEST_WAVES_V2: "questWavesV2",
  REMOVE_OVERCOSTED: "removeOvercosted",
  QUEST_PEN_PAPER: "questPenPaper",
  WF_V3: "warfleets_v3",
  PRODUCT_INFO: "productInfo",
  SHOP_PHYSICAL: "shopPhysical"
};

const ENABLE_WF = isFeatureEnabled(FEATURE_FLAGS.WF_V3);

export const STORAGE_KEYS = {
  GENERIC_MODE: "af_genericModeNew",
  SHOW_BASE_SIZES: "af_showBaseSize",
  SHOW_POINTS_REMAINING: "af_showPointsRemaining",
  SHOW_PRODUCT: "af_showProduct",
  SHOW_HALF_DISTANCES: "af_showHalfDistances",
  REMOVE_ALIAS_MODE: "af_removeAliasMode",
  HOLIDAY_MODE: "af_holidayMode",
  DARK_MODE: "af_darkMode",
  LAST_VERSION: "lastVersion",
  VIEW_PREFS: "view_prefs",
  QUEST_HEROES: "AF_QuestHeroes",
  PUBLISH_IGNORE_PROPS: "af_publishIgnoreProps",
  QUEST_SKIP_TUTORIAL: "af_questSkipTutorial",
  BETA_SIGNUP_IGNORE: "af_betaSignupIgnore",
  BETA_SIGNUP_SHOWN: "af_betaSignupShown",
  BETA_PATCH_NOTES_1: "af_betaPatchNotes1"
};

export const COOKIE_KEYS = {
  AFS_TOKEN: "afs_token",
  LABS_TERMS_ACCEPTED: "labs_terms_accepted"
};

export const QueryKeys = {
  BLOCKED_USERS: "blockedUsers",
  NOTIFICATIONS: "notifications",
  RANGERS: "rangers",
  RANGER_APPLICATIONS: "rangerApplications",
  RANGER_EVENT_REPORTS: "rangerEventReports",
  RANGER_UPDATE_AND_REPLY_REPORTS: "rangerUpdateAndReplyReports",
  RANGER_SUPPORT_REQUESTS: "rangerSupportRequests",
  RANGER_CHATS: "rangerChats",
  RANGER_ADMIN_CHATS: "rangerAdminChats",
  RANGER_EVENTS: "rangerEvents",
  RANGER_BOOKMARKS: "rangerBookmarks",
  RANGER_PENDING_COMPLETION_REPORTS: "rangerPendingCompletionReports",
  RANGER_COMPLETION_REPORTS: "rangerUnconfirmedCompletionReports",
  RANGER_PAST_COMPLETION_REPORTS: "rangerPastCompletionReports",
  RANGER_USER_DETAILS: "rangerUserDetails",
  RANGERS_INACTIVE: "rangersInactive",
  CMS_ITEMS_IMAGES: "CmsItemsImages",
  CMS_ITEMS_MARKDOWN: "CmsItemsMarkdown",
  CMS_SLOTS: "CmsSlots",
  PUBLISHED_BOOK: "publishedBook",
  BOOK_INFO: "bookInfo",
  SPECIAL_RULES: "specialRules",
  OFFICIAL_ARMY_BOOKS: "officialArmyBooks",
  ARMY_BOOK: "armyBook",
  ADMIN_RULES: "admin-rules",
  OFFICIAL_BOOK_RULES: "official-books-rules",
  ADMIN_WEAPONS: "admin-weapons",
  ADMIN_QUEST_SKILLS: "admin-quest-skills",
  ADMIN_QUEST_CONSUMABLES: "admin-quest-consumables",
  ADMIN_QUEST_RULES: "admin-quest-rules",
  RULE_GEN_SECTIONS: "rule-gen-sections",
  RULE_GEN_PARAMS: "rule-gen-params",
  CAMPAIGN_TRAITS: "campaign-traits",
  BETA_SIGNUP: "beta-signup"
};

export const OPR_USER_UUID = "1is8a3JZYgw";

export default class Globals {
  public static gameSystem: string;
}

export const Roles = {
  FreeAccess: "free-access",
  Admin: "admin",
  AdminUsers: "admin:users",
  AdminBooks: "admin:books",
  AdminModeration: "admin:moderation",
  AdminRanger: "admin:ranger",
  Patreon: "patreon",
  ArmyBooks: "army-books",
  CreatorVerified: "creator:verified",
  CreatorStar: "creator:star",
  CreatorPartner: "creator:partner",
  Ranger: "ranger",
  FeaturePreview: "feature-preview"
};

export const gameSystems = {
  gf: {
    id: GameSystems.gf,
    key: "GF",
    name: "Grimdark Future",
    slug: "grimdark-future",
    armyBookBuilderEnabled: true,
    searchEnabled: true,
    engineId: GameEngines.Core,
    universeId: Universes.grimdark
  },
  gff: {
    id: GameSystems.gff,
    key: "GFF",
    name: "Grimdark Future: Firefight",
    slug: "grimdark-future-firefight",
    armyBookBuilderEnabled: true,
    searchEnabled: true,
    engineId: GameEngines.Core,
    universeId: Universes.grimdark
  },
  gfsq: {
    id: GameSystems.gfsq,
    key: "GFSQ",
    name: "Grimdark Future: Star Quest",
    slug: "grimdark-future-star-quest",
    armyBookBuilderEnabled: true,
    searchEnabled: true,
    afLoadDisabled: true,
    engineId: GameEngines.Core,
    universeId: Universes.grimdark
  },
  gfsqai: {
    id: GameSystems.gfsqai,
    key: "GFSQAI",
    name: "Grimdark Future: Star Quest AI",
    slug: "grimdark-future-star-quest-ai",
    armyBookBuilderEnabled: true,
    searchEnabled: false,
    afLoadDisabled: true,
    engineId: GameEngines.Core,
    universeId: Universes.grimdark
  },
  aof: {
    id: GameSystems.aof,
    key: "AOF",
    name: "Age of Fantasy",
    slug: "age-of-fantasy",
    armyBookBuilderEnabled: true,
    searchEnabled: true,
    engineId: GameEngines.Core,
    universeId: Universes.fantasy
  },
  aofs: {
    id: GameSystems.aofs,
    key: "AOFS",
    name: "Age of Fantasy: Skirmish",
    slug: "age-of-fantasy-skirmish",
    armyBookBuilderEnabled: true,
    searchEnabled: true,
    engineId: GameEngines.Core,
    universeId: Universes.fantasy
  },
  aofr: {
    id: GameSystems.aofr,
    key: "AOFR",
    name: "Age of Fantasy: Regiments",
    slug: "age-of-fantasy-regiments",
    armyBookBuilderEnabled: true,
    searchEnabled: true,
    engineId: GameEngines.Core,
    universeId: Universes.fantasy
  },
  aofq: {
    id: GameSystems.aofq,
    key: "AOFQ",
    name: "Age of Fantasy: Quest",
    slug: "age-of-fantasy-quest",
    armyBookBuilderEnabled: true,
    searchEnabled: true,
    afLoadDisabled: true,
    engineId: GameEngines.Core,
    universeId: Universes.fantasy
  },
  aofqai: {
    id: GameSystems.aofqai,
    key: "AOFQAI",
    name: "Age of Fantasy: Quest AI",
    slug: "age-of-fantasy-quest-ai",
    armyBookBuilderEnabled: true,
    searchEnabled: false,
    afLoadDisabled: true,
    engineId: GameEngines.Core,
    universeId: Universes.fantasy
  },
  ftl: {
    id: GameSystems.ftl,
    key: "FTL",
    name: "Grimdark Future: Warfleets",
    slug: "grimdark-future-warfleets2",
    armyBookBuilderEnabled: false,
    searchEnabled: false,
    engineId: GameEngines.Warfleets,
    universeId: Universes.grimdark
  },
  gfwf: {
    id: GameSystems.gfwf,
    key: "GFWF",
    name: "Grimdark Future: Warfleets",
    slug: "grimdark-future-warfleets3",
    armyBookBuilderEnabled: ENABLE_WF,
    searchEnabled: true,
    engineId: GameEngines.Warfleets,
    universeId: Universes.grimdark
  }
};

export const baseSizeOptions = [
  { round: { text: "No Base", value: "" }, square: { text: "No Base", value: "" } },
  { round: { text: "25mm", value: "25" }, square: { text: "25mm", value: "25" } },
  { round: { text: "32mm", value: "32" }, square: { text: "30mm", value: "30" } },
  { round: { text: "40mm", value: "40" }, square: { text: "40mm", value: "40" } },
  { round: { text: "50mm", value: "50" }, square: { text: "50mm", value: "50" } },
  { round: { text: "60mm", value: "60" }, square: { text: "50mm", value: "50" } },
  { round: { text: "60x35mm", value: "60x35" }, square: { text: "60x30mm", value: "60x30" } },
  { round: { text: "75x46mm", value: "75x46" }, square: { text: "75x50mm", value: "75x50" } },
  { round: { text: "90x52mm", value: "90x52" }, square: { text: "100x50mm", value: "100x50" } },
  { round: { text: "105x70mm", value: "105x70" }, square: { text: "100x50mm", value: "100x50" } },
  { round: { text: "120x92mm", value: "120x92" }, square: { text: "100x60mm", value: "100x60" } },
  { round: { text: "160x122mm", value: "160x122" }, square: { text: "175x125mm", value: "175x125" } }
];

export const RuleTags = {
  MeleeBalval: "melee-balval",
  SkirmishRemoveUnit: "skirmish-remove-unit",
  SkirmishRemoveRule: "skirmish-remove-rule"
};

export enum GenericModes {
  Generic = 0,
  LoreNaming = 1
}
